import { useEffect, useRef, useState, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import {
  Maximize2, MoveHorizontal, MoveVertical, ZoomIn, ZoomOut, RotateCw, RefreshCw,
  ChevronLeft, ChevronRight, FileText,
} from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ICON = "h-3.5 w-3.5";

export default function DocumentViewer({ url, type, filename, subfolder }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [dims, setDims] = useState(null);        // rendered page natural dims
  const [container, setContainer] = useState({ w: 800, h: 520 });
  const [fit, setFit] = useState("page");         // page | width | height
  const [zoom, setZoom] = useState(1);
  const [rotate, setRotate] = useState(0);
  const isPdf = (type || "").includes("pdf");

  useEffect(() => { setPage(1); setZoom(1); setRotate(0); setFit("page"); }, [url]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setContainer({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rotated = rotate % 180 !== 0;
  const natW = dims ? (rotated ? dims.height : dims.width) : container.w;
  const natH = dims ? (rotated ? dims.width : dims.height) : container.h;

  const baseWidth = useCallback(() => {
    if (!dims) return container.w - 24;
    const cw = container.w - 24, ch = container.h - 24;
    if (fit === "width") return cw;
    if (fit === "height") return natW * (ch / natH);
    return Math.min(cw, natW * (ch / natH)); // page
  }, [dims, container, fit, natW, natH]);

  const renderWidth = Math.max(80, baseWidth() * zoom);

  const ctrl = (label, testid, icon, onClick, active) => (
    <Button size="sm" variant={active ? "default" : "outline"} onClick={onClick}
      className="rounded-full h-7 px-2.5 text-[11px]" data-testid={testid}>
      {icon}<span className="ml-1 hidden sm:inline">{label}</span>
    </Button>
  );

  return (
    <div className="rounded-lg border border-border overflow-hidden" data-testid="document-viewer">
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 bg-muted/40">
        <FileText className="h-4 w-4 text-primary shrink-0" />
        <span className="text-sm font-semibold truncate flex-1">{filename || "Dokumen"}</span>
        {subfolder && (
          <span className="shrink-0 px-2 py-0.5 rounded text-[11px] font-medium bg-primary/10 text-primary border border-primary/20">
            📁 {subfolder}
          </span>
        )}
      </div>
      <div className="px-2 py-1.5 border-b border-border flex items-center gap-1.5 flex-wrap bg-background">
        {ctrl("Fit Page", "viewer-fit-page", <Maximize2 className={ICON} />, () => { setFit("page"); setZoom(1); }, fit === "page")}
        {ctrl("Fit Width", "viewer-fit-width", <MoveHorizontal className={ICON} />, () => { setFit("width"); setZoom(1); }, fit === "width")}
        {ctrl("Fit Height", "viewer-fit-height", <MoveVertical className={ICON} />, () => { setFit("height"); setZoom(1); }, fit === "height")}
        <span className="w-px h-4 bg-border mx-0.5" />
        {ctrl("Zoom Out", "viewer-zoom-out", <ZoomOut className={ICON} />, () => setZoom((z) => Math.max(0.25, +(z - 0.2).toFixed(2))))}
        <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-center" data-testid="viewer-zoom-level">{Math.round(zoom * 100)}%</span>
        {ctrl("Zoom In", "viewer-zoom-in", <ZoomIn className={ICON} />, () => setZoom((z) => Math.min(5, +(z + 0.2).toFixed(2))))}
        {ctrl("Reset", "viewer-reset", <RefreshCw className={ICON} />, () => { setZoom(1); setRotate(0); setFit("page"); })}
        {ctrl("Rotate", "viewer-rotate", <RotateCw className={ICON} />, () => setRotate((r) => (r + 90) % 360))}
        {isPdf && numPages > 1 && (
          <div className="flex items-center gap-1 ml-auto">
            <Button size="sm" variant="outline" className="rounded-full h-7 w-7 p-0" disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="viewer-prev-page"><ChevronLeft className={ICON} /></Button>
            <span className="text-[11px] tabular-nums text-muted-foreground px-1" data-testid="viewer-page-indicator">Halaman {page} dari {numPages}</span>
            <Button size="sm" variant="outline" className="rounded-full h-7 w-7 p-0" disabled={page >= numPages}
              onClick={() => setPage((p) => Math.min(numPages, p + 1))} data-testid="viewer-next-page"><ChevronRight className={ICON} /></Button>
          </div>
        )}
      </div>
      <div ref={containerRef} className="h-[520px] bg-muted/20 overflow-auto scrollbar-thin flex items-start justify-center p-3">
        {!url ? (
          <span className="text-sm text-muted-foreground self-center">Memuat pratinjau...</span>
        ) : isPdf ? (
          <Document file={url} onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            loading={<span className="text-sm text-muted-foreground self-center">Memuat PDF...</span>}
            error={<span className="text-sm text-red-500 self-center">Gagal memuat PDF.</span>}>
            <Page pageNumber={page} width={renderWidth} rotate={rotate}
              onLoadSuccess={(p) => setDims({ width: p.originalWidth, height: p.originalHeight })}
              renderTextLayer={false} renderAnnotationLayer={false} />
          </Document>
        ) : (
          <img src={url} alt={filename} data-testid="viewer-image"
            onLoad={(e) => setDims({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
            style={{ width: renderWidth, transform: `rotate(${rotate}deg)`, transformOrigin: "top center", height: "auto" }} />
        )}
      </div>
    </div>
  );
}
